import type { Language } from '../../../i18n';

export type UnitLocale = Language;

interface ParseLengthOptions {
  locale: UnitLocale;
  defaultUnit?: 'mm' | 'm';
}

interface FormatLengthOptions {
  locale: UnitLocale;
}

const METER_UNITS = new Set(['m', 'м']);
const MILLIMETER_UNITS = new Set(['mm', 'мм']);

function decimalSeparatorFor(locale: UnitLocale): ',' | '.' {
  return locale === 'en' ? '.' : ',';
}

function normalizeLocale(locale: string | undefined): UnitLocale {
  return locale === 'ru' || locale === 'en' ? locale : 'es';
}

function formatNumber(value: number, locale: UnitLocale, fractionDigits: number): string {
  const separator = decimalSeparatorFor(locale);
  const fixed = value.toFixed(fractionDigits);
  const trimmed = fixed.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
  return separator === ',' ? trimmed.replace('.', ',') : trimmed;
}

export function normalizeUnitLocale(locale: string | undefined): UnitLocale {
  return normalizeLocale(locale?.split('-')[0]);
}

export function parseLengthMm(input: string, options: ParseLengthOptions): number | null {
  const text = input.trim().toLowerCase();
  if (!text) return null;

  const match = text.match(/^(\d+(?:[.,]\d+)?)\s*(mm|мм|m|м)?$/);
  if (!match) return null;

  const unit = match[2];
  const hasDecimal = /[.,]/.test(match[1]);
  const decimalSeparator = decimalSeparatorFor(options.locale);
  const alternateSeparator = decimalSeparator === ',' ? '.' : ',';
  const normalizedNumber = match[1]
    .replace(decimalSeparator, '.')
    .replace(alternateSeparator, '.');
  const value = Number(normalizedNumber);
  if (!Number.isFinite(value) || value <= 0) return null;

  if (unit && METER_UNITS.has(unit)) return value * 1000;
  if (unit && MILLIMETER_UNITS.has(unit)) return value;

  // Ambiguous decimal values without a unit are treated as meters.
  // Whole numbers without a unit are treated as millimeters.
  const defaultUnit = options.defaultUnit ?? (hasDecimal ? 'm' : 'mm');
  return defaultUnit === 'm' ? value * 1000 : value;
}

export function formatLengthMm(mm: number, options: FormatLengthOptions): string {
  if (!Number.isFinite(mm)) return '';
  return `${formatNumber(mm, options.locale, 0)} mm`;
}

export function formatScaleMmPerPx(mmPerPx: number, locale: UnitLocale): string {
  return `${formatNumber(mmPerPx, locale, 2)} mm`;
}
